/**
 * CanvasContext
 *
 * Provides Salesforce Canvas session data (user info, OAuth token, instance URL, and
 * Canvas client object) to all child components.
 *
 * On mount the provider:
 *  - Detects whether the app is running inside a Salesforce Canvas iframe.
 *  - If outside SF (local dev): enables fakes via the Testing module so the
 *    Authentication singleton returns mock data.
 *  - Calls Authentication.getWholeSignedRequest() which uses the Canvas SDK's
 *    refreshSignedRequest callback (or the mock path) to obtain and decode the
 *    signed request payload.
 *  - Stores the decoded payload in React context for all consumers.
 */

import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { CanvasState } from "../interfaces/canvas.interface";
import { Authentication, Testing } from "../services/canvas";
import { isInSalesforce, shouldUseMockCanvas } from "../utils/canvas-config";
import logger from "../utils/logger";

// Canvas event names to subscribe to from the Salesforce parent container.
const CANVAS_EVENT_NAME = "pacProviderMatching.event";

const CanvasContext = createContext<CanvasState | undefined>(undefined);

interface CanvasProviderProps {
  children: ReactNode;
}

export const CanvasProvider: FC<CanvasProviderProps> = ({ children }) => {
  const [state, setState] = useState<CanvasState>({
    isInSalesforce: false,
    isLoading: true,
    user: null,
    oauthToken: null,
    instanceUrl: null,
    client: null,
    signedRequest: null,
    error: null,
    canvasEvent: null,
    publishToSalesforce: () => {},
  });

  // Keep a ref to the client so publishToSalesforce always has the latest
  const clientRef = useRef(state.client);

  // Stable callback for handling incoming Canvas events from the SF parent container.
  const handleCanvasEvent = useCallback((event: unknown) => {
    logger.info({
      text: `Canvas event received: ${CANVAS_EVENT_NAME}`,
      location: "CanvasProvider.initialize",
    });
    setState((prev) => {
      return {
        ...prev,
        canvasEvent: {
          name: CANVAS_EVENT_NAME,
          payload: event,
        },
      };
    });
  }, []);

  // Stable callback for publishing events to the SF parent container.
  const publishToSalesforce = useCallback((name: string, payload: unknown) => {
    const client = clientRef.current;
    if (!client || typeof Sfdc === "undefined") {
      logger.warn({
        text: `Cannot publish to Salesforce — client not available. Event: ${name}`,
        location: "CanvasProvider.publishToSalesforce",
      });
      return;
    }
    logger.info({
      text: `Publishing event to Salesforce: ${name}`,
      location: "CanvasProvider.publishToSalesforce",
    });
    Sfdc.canvas.client.publish(client, { name, payload });
  }, []);

  useEffect(() => {
    const initialize = async () => {
      try {
        // Only load the Canvas SDK when actually running inside Salesforce.
        // The SDK is side-effectful (populates the global `Sfdc` object) and
        // potentially large, so non-Canvas users should not pay the cost.
        // It cannot be a top-level import because the SDK accesses `window`
        // immediately, which breaks Next.js server-side rendering.
        if (!shouldUseMockCanvas()) {
          logger.info({
            text: "Loading Salesforce Canvas SDK. shouldUseMockCanvas is false.",
            location: "CanvasProvider.initialize",
          });
          await import("@salesforce/canvas-js-sdk");
        }

        // Enable fakes when outside Salesforce so Authentication returns mock data
        if (shouldUseMockCanvas()) {
          logger.info({
            text: "Enabling mock Canvas environment. shouldUseMockCanvas is true.",
            location: "CanvasProvider.initialize",
          });
          Testing.enableFakes();
        }

        const signedRequest = await Authentication.getWholeSignedRequest();
        const inSF = isInSalesforce();

        // Log only non-sensitive fields
        logger.info({
          text: `Canvas signed request received. isInSalesforce: ${inSF}`,
          value: signedRequest.client?.instanceUrl,
          location: "CanvasProvider.initialize",
        });

        setState({
          isInSalesforce: inSF,
          isLoading: false,
          user: signedRequest.context.user,
          oauthToken: signedRequest.client.oauthToken,
          instanceUrl: signedRequest.client.instanceUrl,
          client: signedRequest.client,
          signedRequest,
          error: null,
          canvasEvent: null,
          publishToSalesforce,
        });

        // Update the ref so publishToSalesforce always has the current client
        clientRef.current = signedRequest.client;

        // Subscribe to Canvas events from the Salesforce parent container.
        // In local dev (fakes mode) this is a no-op since Sfdc.canvas.client
        // is not available — events can be simulated via Testing utilities.
        if (inSF && typeof Sfdc !== "undefined") {
          Sfdc.canvas.client.subscribe(signedRequest.client, {
            name: CANVAS_EVENT_NAME,
            onData: handleCanvasEvent,
          });
          logger.info({
            text: `Subscribed to Canvas events: ${CANVAS_EVENT_NAME}`,
            location: "CanvasProvider.initialize",
          });

          // Send a ready signal back to Salesforce so parent containers know we are ready to receive events.
          Sfdc.canvas.client.publish(signedRequest.client, {
            name: "pacProviderMatching.ready",
            payload: {
              message: "OBC Self Scheduling app is ready",
              timestamp: new Date().toISOString(),
            },
          });
          logger.info({
            text: "Sent pacProviderMatching.ready signal to Salesforce",
            location: "CanvasProvider.initialize",
          });
        }
      } catch (err) {
        logger.error({
          text: "Failed to initialize Canvas auth",
          error: err instanceof Error ? err : new Error(String(err)),
          location: "CanvasProvider.initialize",
        });
        setState((prev) => {
          return {
            ...prev,
            isLoading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          };
        });
      }
    };

    // Safety timeout: if initialization hangs, forcibly set isLoading to false after 10 seconds.
    // This prevents dependent flows (e.g., non-Canvas posting in GetStartedForm) from blocking indefinitely.
    const timeoutId = setTimeout(() => {
      setState((prev) => {
        if (prev.isLoading) {
          logger.warn({
            text: "Canvas initialization timeout: exceeded 10 seconds. Forcing isLoading to false.",
            location: "CanvasProvider.initialize",
          });
          return {
            ...prev,
            isLoading: false,
            error: new Error("Canvas initialization timeout"),
          };
        }
        return prev;
      });
    }, 10000);

    initialize().finally(() => clearTimeout(timeoutId));

    return () => {
      clearTimeout(timeoutId);
      // Clean up fakes on unmount
      if (shouldUseMockCanvas()) {
        Testing.disableFakes();
        Testing.clearMocks();
      }
    };
  }, []);

  return <CanvasContext.Provider value={state}>{children}</CanvasContext.Provider>;
};

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

/**
 * Returns the current Canvas session state.
 * Must be called from within a `<CanvasProvider>`.
 */
export const useCanvasContext = (): CanvasState => {
  const ctx = useContext(CanvasContext);
  if (ctx === undefined) {
    throw new Error("useCanvasContext must be used within a CanvasProvider");
  }
  return ctx;
};

export default CanvasContext;
